import type { StorySlide } from "@/lib/mentor-stories/story-content";
import { fetchApi } from "./index";

export type MentorStoryImage = StorySlide;

export interface MentorStory {
  id: string;
  mentor_id: string;
  story_type: "first_moment";
  images: MentorStoryImage[];
  created_at: string;
  updated_at: string;
}

export interface PublishedMentorStory {
  story_type: "first_moment";
  images: MentorStoryImage[];
}

export const getMentorStories = async (mentorId: string) => {
  const { data } = await fetchApi<PublishedMentorStory>({
    url: `/v2/users/${mentorId}/story`,
    method: "GET",
  });
  return data.images.length === 0 ? null : data;
};

export const getMentorStoryPresignedUrl = async (
  filename: string,
  contentType: string,
  mentorId?: string,
) => {
  const { data } = await fetchApi<{ key: string; url: string; expiresIn: number }>({
    url: "/v2/mentor/stories/presigned-url",
    method: "GET",
    params: { filename, contentType, ...(mentorId ? { mentorId } : {}) },
  });
  return data;
};

export const uploadMentorStoryFile = async (file: File) => {
  const presigned = await getMentorStoryPresignedUrl(file.name, file.type);
  const upload = await fetch(presigned.url, {
    body: file,
    headers: { "Content-Type": file.type },
    method: "PUT",
  });
  if (!upload.ok) throw new Error("Could not upload the story file");
  return {
    key: presigned.key,
    url: presigned.url.split("?")[0] ?? presigned.url,
  };
};

/** @deprecated Direct write of published slides. Story review approval publishes them now. */
export const saveMentorStory = async (
  mentorId: string,
  body: { storyType: string; images: MentorStoryImage[] },
) => {
  const { data } = await fetchApi<MentorStory>({
    url: "/v2/mentor/stories",
    method: "POST",
    data: { ...body, mentorId },
  });
  return data;
};

/** @deprecated Direct delete of a published slide. Story review takedown clears them now. */
export const deleteMentorStoryImage = async (mentorId: string, imageKey: string) => {
  const { data } = await fetchApi<MentorStory>({
    url: `/v2/mentor/stories/images/${encodeURIComponent(imageKey)}`,
    method: "DELETE",
    params: { mentorId, storyType: "first_moment" },
  });
  return data;
};
